import React from 'react';

import css from './PageHeader.module.css';

/**
 * PageHeader — eyebrow pill + H1 + lead paragraph.
 *
 * The first block on every Wheelplace-branded page (Om, Kontakt, Terms,
 * Privacy, future logged-in pages). Previously inlined 4+ times with
 * slightly different class names; now canonical.
 *
 * API (all optional):
 *   - eyebrow:  small uppercase pill text ("Support · Kundtjänst", "Om oss")
 *   - title:    H1 headline (required for a real PageHeader, but typed
 *               as optional so it degrades gracefully in preview contexts)
 *   - lead:     subtitle / intro paragraph (string or node for inline
 *               links/formatting)
 *   - align:    "left" (default) | "center" — controls text alignment and
 *               pushes the eyebrow's pill to sit on the same axis.
 *   - size:     "compact" (24/28px — for pages nested inside
 *               LayoutSideNavigation where a 40px title dominates)
 *               | "default" (32/40px — standalone public pages)
 *               | "hero" (36/52px — thesis editorial openers like Om)
 *   - children: any extra content beneath the lead (e.g. a CTA row).
 *
 * Keeping the surface tiny on purpose. If a page needs special layout
 * (e.g. the Om page's thesis H1 at 52px), it's fine to inline — the
 * primitive captures the 80% common case.
 */

const PageHeader = ({
  eyebrow,
  title,
  lead,
  align = 'left',
  size = 'default',
  className,
  children,
}) => {
  const alignClass = align === 'center' ? css.alignCenter : css.alignLeft;
  const sizeClass =
    size === 'hero'
      ? css.sizeHero
      : size === 'compact'
      ? css.sizeCompact
      : css.sizeDefault;
  const rootClass = [css.root, alignClass, sizeClass, className]
    .filter(Boolean)
    .join(' ');

  return (
    <header className={rootClass}>
      {eyebrow ? <span className={css.eyebrow}>{eyebrow}</span> : null}
      {title ? <h1 className={css.title}>{title}</h1> : null}
      {lead ? <p className={css.lead}>{lead}</p> : null}
      {children}
    </header>
  );
};

export default PageHeader;
